import React, { useEffect, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

const Hero = ({ t }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      const elements = heroRef.current.querySelectorAll('.fade-in, .scroll-fade-up, .scroll-fade-left, .scroll-fade-right, .scroll-zoom, .scroll-rotate');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const handleCallClick = () => {
    window.location.href = `tel:${t.hero.phone}`;
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${t.hero.email}`;
  };

  return (
    <section id="home" className="hero-section" ref={heroRef}>
      <Container>
        <Row className="align-items-center">
          <Col lg={6} className="hero-content">
            <h1 className="hero-title scroll-fade-up animate-fade-in-up title-enhance">{t.hero.name}</h1>
            <h3 className="hero-subtitle scroll-fade-left fade-in-delay-1">{t.hero.qualification}</h3>
            <p className="lead mb-4 scroll-fade-up fade-in-delay-2">{t.hero.experience}</p>
            <p className="hero-tagline mb-4 text-muted scroll-fade-right fade-in-delay-3">{t.hero.tagline}</p>
            
            <div className="d-flex flex-wrap gap-3 mb-4 scroll-fade-up fade-in-delay-4 stagger-animation">
              <Button 
                variant="primary" 
                size="lg" 
                className="btn-primary-custom hover-lift hover-glow animate-bounce-in magnetic"
                style={{'--i': 0}}
                onClick={handleCallClick}
              >
                <FaPhone className="me-2 animate-heartbeat" />
                {t.hero.callNow}
              </Button>
              <Button 
                variant="success" 
                size="lg" 
                className="btn-success-custom hover-lift hover-bounce animate-elastic"
                style={{'--i': 1}}
                onClick={() => isHomePage ? navigate('/bookings') : document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <FaCalendarAlt className="me-2 animate-swing" />
                {t.hero.bookAppt}
              </Button>
            </div>
          </Col>
          
          <Col lg={6} className="hero-image">
            <div className="text-center scroll-fade-right">
              <img 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                alt="Pritish Nanda Pradhan"
                className="img-fluid hover-tilt float-slow"
                style={{ maxHeight: '500px', objectFit: 'cover' }}
              />
            </div>
            
            <div className="contact-info scroll-fade-up fade-in-delay-5 stagger-animation">
              <div className="contact-info-item hover-lift hover-glow animate-bounce-in" 
                   style={{'--i': 0, cursor: 'pointer'}} 
                   onClick={handleCallClick}>
                <div className="icon animate-heartbeat">
                  <FaPhone />
                </div>
                <div>
                  <h6 className="mb-0">Phone</h6>
                  <p className="mb-0">{t.hero.phone}</p>
                </div>
              </div>
              
              <div className="contact-info-item hover-lift hover-slide animate-zoom-in" 
                   style={{'--i': 1, cursor: 'pointer'}} 
                   onClick={handleEmailClick}>
                <div className="icon animate-wobble">
                  <FaEnvelope />
                </div>
                <div>
                  <h6 className="mb-0">Email</h6>
                  <p className="mb-0">{t.hero.email}</p>
                </div>
              </div>
              
              <div className="contact-info-item hover-lift hover-rotate animate-flip-in" 
                   style={{'--i': 2}}>
                <div className="icon float-medium">
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <h6 className="mb-0">Address</h6>
                  <p className="mb-0">{t.hero.address}</p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Hero;